import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import {
    createPhysicalActivity,
    updatePhysicalActivity
} from '../../../../store/participantService';
import { todayDate } from '../../../../utils/dateFunctions';
import { regExps } from '../../../../utils/formValidation/validationPatterns';
import DateSelect from '../../../formElements/DateSelect';
import TextInputField from '../../../formElements/TextInputField';
import FormActions from '../../../ui/formActions/FormActions';

import { validate } from './validatePhysicalActivity';

import app from '../../../../app.module.css';
import form from '../../../../commonStyles/formStyles.module.css';

const CreatePhysicalActivity = ({ participant, onClose, isDisabled, formType, row = {} }) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const _MESSAGE = `Participant physical activity has been ${
        formType === 'create' ? 'added' : 'updated'
    }`;
    const initialState = {
        id: '',
        eventDate: todayDate(),
        vigorousActivityMinutes: '',
        moderateActivityMinutes: '',
        walkingMinutes: '',
        participantId: ''
    };
    const [newEntry, setNewEntry] = useState(initialState);
    const [initialRow, setInitialRow] = useState({});
    const [errors, setErrors] = useState({});
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);

    // USE EFFECTS
    useEffect(() => {
        if (formType === 'edit') {
            setNewEntry(row);
            setInitialRow(row);
        }
    }, [row]);

    useEffect(() => {
        setIsButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    // HELPER FNS
    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { ...errors[key], error: false } }));
    };

    // EVENT HANDLERS
    const onUpdate = (key, value) => {
        clearError(key);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onUpdateActivity = (e) => {
        onUpdate(e.target.id, e.target.value.replace(regExps.DIGITS_R, ''));
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setIsButtonDisabled(true);
        const newErrors = validate(newEntry, formType, initialRow);
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        const payload = { ...newEntry, participantId: participant?.id };
        formType === 'create'
            ? dispatch(createPhysicalActivity(payload, _MESSAGE))
            : dispatch(updatePhysicalActivity(payload.id, payload, _MESSAGE));
        onClose();
    };

    // RENDER
    return (
        <div className={`${form.formWrapper} ${app.fadein}`}>
            <form
                className={form.form}
                onSubmit={onSubmit}
                data-testid="edit_physical_activity_form">
                <h2 className={app.sectionHeading}>
                    {formType === 'create'
                        ? 'Add a New Physical Activity'
                        : 'Edit Physical Activity'}
                </h2>
                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <DateSelect
                            value={newEntry.eventDate}
                            label="Event Date"
                            disabled={isDisabled}
                            mandatory={true}
                            error={errors.eventDate}
                            onDateChange={(date) => onUpdate('eventDate', date)}
                        />

                        <div className={form.fullWidth}>
                            <div className={form.fullWidthSection}>
                                <TextInputField
                                    id="vigorousActivityMinutes"
                                    label="Vigorous activity in past seven days (minutes)"
                                    placeholder="Enter vigorous activity..."
                                    mandatory={true}
                                    maxLength={5}
                                    disabled={isDisabled}
                                    value={newEntry.vigorousActivityMinutes}
                                    error={errors.vigorousActivityMinutes}
                                    onChange={onUpdateActivity}
                                />
                            </div>

                            <div className={form.fullWidthSection}>
                                <TextInputField
                                    id="moderateActivityMinutes"
                                    label="Moderate activity in past seven days (minutes)"
                                    placeholder="Enter moderate activity..."
                                    mandatory={true}
                                    maxLength={5}
                                    disabled={isDisabled}
                                    value={newEntry.moderateActivityMinutes}
                                    error={errors.moderateActivityMinutes}
                                    onChange={onUpdateActivity}
                                />
                            </div>

                            <div className={form.fullWidthSection}>
                                <TextInputField
                                    id="walkingMinutes"
                                    label="Walking in past seven days (minutes)"
                                    placeholder="Enter walking..."
                                    mandatory={true}
                                    maxLength={5}
                                    disabled={isDisabled}
                                    value={newEntry.walkingMinutes}
                                    customLabelClass="addPadding"
                                    error={errors.walkingMinutes}
                                    onChange={onUpdateActivity}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <FormActions
                    onClose={onSubmit}
                    onCancel={onClose}
                    disabled={isButtonDisabled || isDisabled}
                    btnText={formType === 'create' ? 'Save' : 'Update'}
                    customClass="noTopBorder"
                    error={errors?.button}
                    clearError={() => clearError('button')}
                />
            </form>
        </div>
    );
};

CreatePhysicalActivity.propTypes = {
    participant: PropTypes.object.isRequired,
    onClose: PropTypes.func,
    isDisabled: PropTypes.bool,
    formType: PropTypes.string,
    row: PropTypes.object
};

export default CreatePhysicalActivity;
